import React, { useEffect, useRef, useState } from "react";
import FeedbackTile from "../../components/FeedbackTile/FeedbackTile";
import "./HearFromOurClients.css";

interface HearFromOurClientsProps {
  hearFromOurClientsRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const feedbackData = [
  {
    feedback: `"JJJM Sun Enterprises has consistently provided reliable and timely delivery services. Their team is professional and always goes the extra mile."`,
    companyName: "Barrelman Corporation",
  },
  {
    feedback: `"The level of customer service and attention to detail we receive from JJJM Sun Enterprises is unmatched. We highly recommend their services."`,
    companyName: "Wartsila Philippines",
  },
  {
    feedback: `"Net Center Logistics values speed and safety. Partnering with JJJM Sun Enterprises has significantly optimized our regional distribution."`,
    companyName: "Net Center Logistics Corporation",
  },
  {
    feedback: `"Their brokerage coordination and transport reliability have made our supply chain operations completely seamless and stress-free."`,
    companyName: "CSP Brokerage",
  },
  {
    feedback: `"Blue Mountains Worldwide relies on dependable partners for wide distribution. JJJM Sun Enterprises exceeds our expectations every single time."`,
    companyName: "Blue Mountains Worldwide Distribution Inc.",
  },
  {
    feedback: `"Link Asia Forwarding relies on efficient transport solutions across Luzon. Their fleet readiness and punctuality are truly top-tier."`,
    companyName: "Link Asia Forwarding Solutions Corp.",
  },
  {
    feedback: `"Teleport Commerce demands rapid and accurate fulfillment. JJJM Sun Enterprises delivers exceptional support for our fast-paced operations."`,
    companyName: "Teleport Commerce Philippines Inc.",
  },
  {
    feedback: `"Airtropolis Consolidator values secure cargo handling. Their professional team and well-maintained fleet guarantee total peace of mind."`,
    companyName: "Airtropolis Consolidator Philippines Inc.",
  },
];

const HearFromOurClients: React.FC<HearFromOurClientsProps> = ({
  hearFromOurClientsRef,
}) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToSlide = (index: number) => {
    if (!carouselRef.current) return;
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile: scroll by individual card width (1 per view)
      const cardWidth = carouselRef.current.clientWidth;
      carouselRef.current.scrollTo({
        left: cardWidth * index,
        behavior: "smooth",
      });
    } else {
      // On desktop: scroll by the card width + gap (3 visible at a time)
      const card = carouselRef.current.children[0] as HTMLElement;
      if (card) {
        const slideWidth = card.offsetWidth + 24; // 24px gap
        carouselRef.current.scrollTo({
          left: slideWidth * index,
          behavior: "smooth",
        });
      }
    }
  };

  // Auto-scroll loop
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % feedbackData.length;
        scrollToSlide(nextIndex);
        return nextIndex;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Sync pagination dots on manual scroll/drag
  const handleScroll = () => {
    if (!carouselRef.current) return;
    const isMobile = window.innerWidth <= 768;
    const scrollPos = carouselRef.current.scrollLeft;

    if (isMobile) {
      const cardWidth = carouselRef.current.clientWidth;
      const newIndex = Math.round(scrollPos / cardWidth);
      if (!isNaN(newIndex) && newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    } else {
      const card = carouselRef.current.children[0] as HTMLElement;
      if (card) {
        const slideWidth = card.offsetWidth + 24;
        const newIndex = Math.round(scrollPos / slideWidth);
        if (!isNaN(newIndex) && newIndex !== activeIndex) {
          setActiveIndex(newIndex);
        }
      }
    }
  };

  return (
    <section
      className="hear-from-our-clients-container"
      id="hearFromOurClients"
      ref={hearFromOurClientsRef}
    >
      <div className="hear-from-our-client-content-container">
        <div className="hear-from-our-satisfied-client-container">
          <div>
            <h1>Hear from our</h1>
            <h1 className="satisfied-clients-label">satisfied clients</h1>
            <p>
              We take pride in delivering exceptional service to our clients.
              Here’s what they have to say about us:
            </p>
          </div>

          <img
            src={require("../../assets/images/Truck2.png")}
            alt="JJJM Sun Truck"
            className="header-truck-img"
          />
        </div>

        {/* Carousel Container */}
        <div
          className="feedback-list-container"
          ref={carouselRef}
          onScroll={handleScroll}
        >
          {feedbackData.map((item, index) => (
            <div className="feedback-item-wrapper" key={index}>
              <FeedbackTile
                feedback={item.feedback}
                companyName={item.companyName}
              />
            </div>
          ))}
        </div>

        {/* Pagination Dots */}
        <div className="carousel-dots">
          {feedbackData.map((_, index) => (
            <span
              key={index}
              className={`dot ${activeIndex === index ? "active" : ""}`}
              onClick={() => {
                setActiveIndex(index);
                scrollToSlide(index);
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HearFromOurClients;